import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider } from './contexts/AuthContext';
import { ThemeProvider } from './contexts/ThemeContext';
import { NotificationProvider } from './contexts/NotificationContext';
import { PWAProvider } from './contexts/PWAContext';
import { FeatureFlagsProvider } from './contexts/FeatureFlagsContext';
import { LoginForm } from './components/auth/LoginForm';
import { RegisterForm } from './components/auth/RegisterForm';
import { EmailActionHandler } from './components/auth/EmailActionHandler';
import { DashboardLayout } from './components/dashboard/DashboardLayout';
import { DashboardHome } from './components/dashboard/DashboardHome';
import { FacilitiesList } from './components/facilities/FacilitiesList';
import { BookingsList } from './components/bookings/BookingsList';
import { HorsesList } from './components/horses/HorsesList';
import { MembersList } from './components/members/MembersList';
import { NotificationCenter } from './components/notifications/NotificationCenter';
import { SettingsLayout } from './components/settings/SettingsLayout';
import { useAuth } from './contexts/AuthContext';
import { AuthLayout } from './components/auth/AuthLayout';
import { TermsModal } from './components/auth/TermsModal';
import { useFeatureFlags } from './contexts/FeatureFlagsContext';
import { useUserRole } from './hooks/useUserRole';
import { ImprintPage } from './components/legal/ImprintPage';
import { PrivacyPage } from './components/legal/PrivacyPage';
import { Footer } from './components/common/Footer';
import { EmailVerificationPrompt } from './components/auth/EmailVerificationPrompt';
import { EmailVerificationHandler } from './components/auth/EmailVerificationHandler';

const PrivateRoute = ({ children }: { children: React.ReactNode }) => {
  const { user, loading, needsTermsAcceptance, acceptTerms, declineTerms } = useAuth();
  const { features } = useFeatureFlags();
  const { isManager } = useUserRole();
  
  if (loading) {
    return <div>Lädt...</div>;
  }

  if (!user) {
    return <Navigate to="/" />;
  }

  // Wartungsmodus-Prüfung
  if (features.maintenanceMode && !isManager) {
    return (
      <div className="min-h-screen bg-gray-100 dark:bg-gray-900 flex items-center justify-center p-4">
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl p-6 max-w-md w-full">
          <h2 className="text-xl font-bold text-gray-900 dark:text-white mb-4">
            System-Wartung
          </h2>
          <p className="text-gray-600 dark:text-gray-400 mb-4">
            Das System befindet sich derzeit im Wartungsmodus. Nur Administratoren haben Zugriff.
            Bitte versuchen Sie es später erneut.
          </p>
          <button
            onClick={() => window.location.href = '/'}
            className="w-full px-4 py-2 bg-indigo-600 text-white rounded-md hover:bg-indigo-700"
          >
            Zurück zum Login
          </button>
        </div>
      </div>
    );
  }

  if (needsTermsAcceptance) {
    return (
      <>
        {children}
        <TermsModal
          isOpen={true}
          onAccept={acceptTerms}
          onDecline={declineTerms}
        />
      </>
    );
  }

  return <>{children}</>;
};

const RequireAuth = ({ children }: { children: React.ReactNode }) => {
  const { user, loading } = useAuth();

  if (loading) {
    return <div>Lädt...</div>;
  }

  if (!user) {
    return (
      <AuthLayout>
        <LoginForm />
      </AuthLayout>
    );
  }

  return <>{children}</>;
};

export default function App() {
  return (
    <AuthProvider>
      <ThemeProvider>
        <NotificationProvider>
          <PWAProvider>
            <FeatureFlagsProvider>
              <Router>
                <div className="min-h-screen flex flex-col">
                  <div className="flex-grow">
                    <Routes>
                      <Route path="/" element={
                        <RequireAuth>
                          <Navigate to="/dashboard" replace />
                        </RequireAuth>
                      } />
                      <Route path="/login" element={
                        <AuthLayout>
                          <LoginForm />
                        </AuthLayout>
                      } />
                      <Route path="/register" element={
                        <AuthLayout>
                          <RegisterForm />
                        </AuthLayout>
                      } />
                      <Route path="/verify-email" element={
                        <AuthLayout>
                          <EmailVerificationPrompt />
                        </AuthLayout>
                      } />
                      <Route path="/verify" element={
                        <AuthLayout>
                          <EmailVerificationHandler />
                        </AuthLayout>
                      } />
                      <Route path="/auth/action" element={<EmailActionHandler />} />
                      <Route path="/dashboard" element={
                        <PrivateRoute>
                          <DashboardLayout />
                        </PrivateRoute>
                      }>
                        <Route index element={<DashboardHome />} />
                        <Route path="facilities" element={<FacilitiesList />} />
                        <Route path="bookings" element={<BookingsList />} />
                        <Route path="horses" element={<HorsesList />} />
                        <Route path="members" element={<MembersList />} />
                        <Route path="notifications" element={<NotificationCenter />} />
                        <Route path="settings" element={<SettingsLayout />} />
                      </Route>
                      <Route path="/impressum" element={<ImprintPage />} />
                      <Route path="/datenschutz" element={<PrivacyPage />} />
                    </Routes>
                  </div>
                  <Footer />
                </div>
              </Router>
            </FeatureFlagsProvider>
          </PWAProvider>
        </NotificationProvider>
      </ThemeProvider>
    </AuthProvider>
  );
}
